// Admin session/token management. The panel signs in against Firebase's REST
// endpoints (no Firebase JS SDK in this bundle), so we persist the refresh token
// and expiry ourselves and mint fresh ID tokens via the securetoken endpoint —
// otherwise the ID token expires after ~1h and every request 401s / the admin is
// force-logged-out mid-session.
import { getConfig } from '../config';
import { EXPIRY_KEY, REFRESH_KEY, TOKEN_KEY } from '../constants';

type SessionTokens = {
  idToken: string;
  refreshToken?: string;
  // Seconds-to-live as returned by Firebase (string or number).
  expiresIn?: string | number;
};

// Refresh a little early so an in-flight request never rides an already-expired token.
const EXPIRY_SKEW_MS = 60_000;
const DEFAULT_TTL_SECONDS = 3600;

// True until the first token request of this page load. A cached ID token can survive a page
// reload (localStorage) yet be rejected server-side — it expired while the tab was closed, or the
// clock skewed — which 401'd the first request of every cold load before the app recovered. So the
// first request after a load mints a fresh token instead of trusting the cached one. Reset to true
// on every full page load (module re-init); stays false across SPA navigation.
let coldStart = true;

export const setSession = ({ idToken, refreshToken, expiresIn }: SessionTokens) => {
  localStorage.setItem(TOKEN_KEY, idToken);
  if (refreshToken) localStorage.setItem(REFRESH_KEY, refreshToken);
  const ttlMs = (Number(expiresIn) || DEFAULT_TTL_SECONDS) * 1000;
  localStorage.setItem(EXPIRY_KEY, String(Date.now() + ttlMs - EXPIRY_SKEW_MS));
  // A just-minted token (login or refresh) is fresh — no cold-start force needed after it.
  coldStart = false;
};

export const clearSession = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_KEY);
  localStorage.removeItem(EXPIRY_KEY);
};

// Serialize concurrent refreshes so a burst of requests triggers a single token mint.
let refreshInFlight: Promise<string | null> | null = null;

const refreshIdToken = async (): Promise<string | null> => {
  const refreshToken = localStorage.getItem(REFRESH_KEY);
  if (!refreshToken) {
    clearSession();
    return null;
  }

  // Distinguish TRANSIENT failures (return null but keep the session, so a later
  // attempt can recover) from DEFINITIVE ones (the refresh token itself is bad —
  // clear the session and force re-login). Nuking a valid session on a network/config
  // blip would needlessly log the admin out.
  let config: Awaited<ReturnType<typeof getConfig>>;
  try {
    config = await getConfig();
  } catch {
    return null; // couldn't load config to even attempt the refresh — transient
  }

  const { apiKey, useEmulators, authEmulatorHost } = config;
  const tokenUrl =
    useEmulators && authEmulatorHost
      ? `http://${authEmulatorHost}/securetoken.googleapis.com/v1/token?key=${apiKey || 'local'}`
      : `https://securetoken.googleapis.com/v1/token?key=${apiKey}`;

  let resp: Response;
  try {
    resp = await fetch(tokenUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: `grant_type=refresh_token&refresh_token=${encodeURIComponent(refreshToken)}`,
    });
  } catch {
    return null; // network blip talking to securetoken — transient
  }

  // A non-ok response means securetoken rejected the refresh token (revoked /
  // invalid_grant) — definitive, so clear.
  if (!resp.ok) {
    clearSession();
    return null;
  }

  // securetoken returns snake_case fields.
  const data = await resp.json();
  if (!data?.id_token) {
    clearSession();
    return null;
  }
  setSession({
    idToken: data.id_token,
    refreshToken: data.refresh_token,
    expiresIn: data.expires_in,
  });
  return data.id_token as string;
};

/**
 * Return a non-expired ID token, refreshing if needed. Returns null when there is
 * no usable session (no token, or a legacy token with no refresh token) — callers
 * treat null as "logged out".
 */
export const getValidToken = async (): Promise<string | null> => {
  // If a refresh is already in flight (a concurrent cold-start / expired request kicked one off),
  // wait for it rather than handing back the cached token that prompted the refresh.
  if (refreshInFlight) return refreshInFlight;

  const token = localStorage.getItem(TOKEN_KEY);
  const hasRefresh = !!localStorage.getItem(REFRESH_KEY);

  // First request of a page load: mint fresh (see `coldStart`) so the first request never rides a
  // stale-but-not-yet-expired cached token. After that, use the cached token until it nears expiry.
  const forceRefresh = coldStart && hasRefresh;
  coldStart = false;

  const expiry = Number(localStorage.getItem(EXPIRY_KEY) || 0);
  if (!forceRefresh && token && Date.now() < expiry) return token;

  // Expired / forcing / a pre-refresh legacy session with no expiry. Need a refresh token to
  // continue; without one, force a fresh login.
  if (!hasRefresh) {
    clearSession();
    return null;
  }

  refreshInFlight = refreshIdToken().finally(() => {
    refreshInFlight = null;
  });
  const refreshed = await refreshInFlight;
  if (refreshed) return refreshed;

  // The cold-start force-refresh is best-effort. A TRANSIENT failure keeps the session (the token
  // is still in storage), so ride the still-valid cached token this load rather than bouncing the
  // admin to login — the existing 401-tolerant flow recovers if it turns out stale. A DEFINITIVE
  // failure (revoked refresh token) clears the session, so there's nothing to fall back to.
  if (forceRefresh && token && Date.now() < expiry && localStorage.getItem(TOKEN_KEY)) {
    return token;
  }
  return refreshed;
};
